import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { apiConfig } from '../../config/api';
import { EventVolunteerDTO, Event } from '../../types/Event';
import ActionButton from '../shared/ActionButton';

interface EventVolunteersProps {
  event: Event;
  isOpen: boolean;
  onClose: () => void;
}

const EventVolunteers: React.FC<EventVolunteersProps> = ({ event, isOpen, onClose }) => {
  const [volunteers, setVolunteers] = useState<EventVolunteerDTO[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchVolunteers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${apiConfig.baseURL}/api/admin/events/${event.id}/volunteers`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setVolunteers(response.data as EventVolunteerDTO[]);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching volunteers:', err);
      if (err.response?.status === 403) {
        setError('You don\'t have permission to view volunteers for this event.');
      } else if (err.response?.status === 404) {
        setError('Event not found.');
      } else {
        setError('Failed to load volunteers. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && event.id) {
      fetchVolunteers();
    }
  }, [event.id, isOpen]);

  const handleEmailVolunteer = (email: string, name: string) => {
    const subject = encodeURIComponent(`Regarding: ${event.title}`);
    const body = encodeURIComponent(`Hi ${name},\n\nThank you for volunteering for "${event.title}".\n\nBest regards`);
    window.open(`mailto:${email}?subject=${subject}&body=${body}`);
  };

  const handleEmailAllVolunteers = () => {
    if (volunteers.length === 0) return;

    const emails = volunteers.map(v => v.email).join(',');
    const subject = encodeURIComponent(`Update: ${event.title}`);
    const body = encodeURIComponent(`Dear Volunteers,\n\nThank you all for volunteering for "${event.title}".\n\nBest regards`);
    window.open(`mailto:?bcc=${emails}&subject=${subject}&body=${body}`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatRoles = (roles: string[] | undefined | null) => {
    if (!roles || !Array.isArray(roles) || roles.length === 0) {
      return 'No roles assigned';
    }
    return roles.join(', ');
  };

  if (!isOpen) return null;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-4xl shadow-xl">
          <div className="text-center py-10">Loading volunteers...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-6xl max-h-[90vh] shadow-xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center mb-6 border-b border-gray-200 pb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Event Volunteers</h2>
            <p className="text-gray-600 mt-1">{event.title}</p>
            <p className="text-sm text-gray-500 mt-1">
              {volunteers.length} volunteer{volunteers.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex space-x-2">
            {volunteers.length > 0 && (
              <ActionButton
                variant="primary"
                size="sm"
                onClick={handleEmailAllVolunteers}
              >
                Email All Volunteers
              </ActionButton>
            )}
            <ActionButton
              variant="secondary"
              size="sm"
              onClick={onClose}
            >
              Close
            </ActionButton>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4">
            {error}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {volunteers.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 text-lg mb-2">No volunteers yet</div>
              <div className="text-gray-500">No one has volunteered for this event.</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      School
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Volunteered
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {volunteers.map((volunteer) => (
                    <tr key={volunteer.userId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {volunteer.firstName} {volunteer.lastName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {volunteer.username}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        <a
                          href={`mailto:${volunteer.email}`}
                          className="text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {volunteer.email}
                        </a>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatRoles(volunteer.roles)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {volunteer.schoolName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatDate(volunteer.volunteeredAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <ActionButton
                          variant="primary"
                          size="sm"
                          onClick={() => handleEmailVolunteer(volunteer.email, volunteer.firstName)}
                        >
                          Email
                        </ActionButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventVolunteers;
